import { Stack, spacing, Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { useChat } from '../../hooks/useChat';
import { useChatUrlInstanceIds } from '../../hooks/useChatUrlInstanceIds';
import { BaseChatBox } from '../chat-messages/ChatCard';
import { GuardianContext } from '../GuardianProvider';
import { Icon } from '../Icon';
import { MessageComposer } from '../message-composer/MessageComposer';
import { XXLargeGradientText, XXLargeText } from '../TextVariants';
import { Alerts } from './Alerts';

const WelcomerBox = styled.div`
  view-transition-name: welcomer;
  display: flex;
  flex-direction: column;
  gap: ${spacing.r16};

  margin-inline: calc(var(--chat-gutter) + var(--chat-inset, 0px));
`;

const AgentCard = styled(BaseChatBox)`
  background-color: ${(p) => p.theme.chatCardGray};
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
`;

export const WelcomeScreen: React.FC = () => {
  const { userName } = useAuth();

  const { selectedAgent } = useContext(GuardianContext);
  const { sendMessage, isResponding } = useChat();
  const sendWithAnimation = useCallback(
    (query: string) => {
      document.startViewTransition?.();
      sendMessage({ query });
    },
    [sendMessage],
  );

  const { selectedInstanceIds } = useChatUrlInstanceIds();

  const isAfterNoon = new Date().getHours() >= 12;
  const greeting = isAfterNoon ? 'Good afternoon' : 'Good morning';
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      overflow="hidden"
      height="100%"
      flexGrow={1}
      style={{ '--chat-inset': '8vw' } as React.CSSProperties}
    >
      <Box display="flex" flexDirection="column" gap={spacing.r16}>
        <WelcomerBox>
          {selectedAgent && !selectedAgent.landingPage ? (
            <AgentCard as="div">
              <Stack gap="r8">
                <Icon name={selectedAgent.icon || 'Info'} size="lg" />
                <Text variant="Larger" color="textPrimary" isEmphazed>
                  {selectedAgent.name}
                </Text>
              </Stack>
              <Text variant="Basic" color="textSecondary">
                {selectedAgent.description}
              </Text>
            </AgentCard>
          ) : (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={spacing.r8}>
                <XXLargeText>
                  {greeting}, <XXLargeGradientText>{userName}</XXLargeGradientText>.
                </XXLargeText>
              </Box>
              <Text variant="Basic" color="textSecondary">
                Learn more about Scality products, anticipate capacity-related issues, and plan corrective actions
                through predictive models and anomaly detection.
              </Text>
              {selectedInstanceIds.length > 0 && (
                <Alerts onSubmit={sendWithAnimation} selectedInstanceIds={selectedInstanceIds} />
              )}
            </>
          )}
        </WelcomerBox>
        <MessageComposer onSubmit={sendWithAnimation} isLoading={isResponding} isFloating />
      </Box>
    </Box>
  );
};
